import {
  FreshServiceProposal,
  FreshServiceProposalWashAlternative,
  ServiceProposalPackage,
} from "./fresh-service-proposal"

/**
 * Check if a wash (by originalWashId) exists in any of the given packages.
 */
export function isWashInPackages(
  originalWashId: string,
  packages: Pick<ServiceProposalPackage, "wash">[],
): boolean {
  return packages.some((pkg) => pkg.wash?.originalWashId === originalWashId)
}

/**
 * Check if all wash alternatives are already included in the given packages.
 * Used to hide the wash alternatives section when they're redundant.
 */
export function areAllWashAlternativesInPackages(
  alternatives: Pick<FreshServiceProposalWashAlternative, "originalWashId">[],
  packages: Pick<ServiceProposalPackage, "wash">[],
): boolean {
  return alternatives.every((alt) =>
    isWashInPackages(alt.originalWashId, packages),
  )
}

/**
 * Check if any of the proposal's available washes (alternatives or single)
 * match a wash in the given packages. Used to avoid double-counting wash prices.
 */
export function isProposalWashInPackages(
  proposal: Pick<FreshServiceProposal, "wash" | "washAlternatives">,
  packages: Pick<ServiceProposalPackage, "wash">[],
): boolean {
  return packages.some((pkg) => {
    if (!pkg.wash) return false

    if (proposal.washAlternatives) {
      return proposal.washAlternatives.alternatives.some(
        (alt) => alt.originalWashId === pkg.wash?.originalWashId,
      )
    }

    if (proposal.wash) {
      return pkg.wash.originalWashId === proposal.wash.originalWashId
    }

    return false
  })
}

/**
 * Resolve selected package references (IDs) to full package objects.
 */
export function resolveSelectedPackages(
  selectedPackages: { originalPackageId: string }[],
  allPackages: ServiceProposalPackage[],
): ServiceProposalPackage[] {
  const result: ServiceProposalPackage[] = []
  for (const sel of selectedPackages) {
    const pkg = allPackages.find(
      (p) => p.originalPackageId === sel.originalPackageId,
    )
    if (pkg) {
      result.push(pkg)
    }
  }
  return result
}

/**
 * A package covers a picked wash when the package includes a wash and the
 * picked one is not more expensive. Mirrors the rule the inspection already
 * saves with, so reception, the receipt editor and the CRM preview all agree.
 */
export function packageCoversWash(
  washPrice: number,
  packageWash: { price: number } | null | undefined,
): boolean {
  if (!packageWash) return false
  return washPrice <= packageWash.price
}
